"use client";

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { SkeletonTableRow } from "@/components/ui-system";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { ThemedSelect, themedSelectMenuClass, themedSelectTriggerClass } from "@/components/ui-system/select";
import {
  createBlocklistEntry,
  deleteBlocklistEntry,
  getDeviceIngestionKeys,
  getSecurityOverview,
  revokeDeviceIngestionKey,
} from "@/lib/api/endpoints/security";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

const surfaceBorder = "var(--card-border)";
const surfaceShadow = "var(--shadow-card)";
const headerBg = "color-mix(in srgb, var(--muted-bg) 55%, var(--card))";

const triggerClassName = themedSelectTriggerClass;
const menuClassName = themedSelectMenuClass;
const optionClassName = "rounded-control text-caption";

function badgeTone(value: string): React.CSSProperties {
  const key = value.toLowerCase();
  if (key === "active" || key === "ok") return { background: "color-mix(in srgb, var(--success) 12%, var(--card))", color: "var(--success)" };
  if (key === "blocked" || key === "revoked") return { background: "color-mix(in srgb, var(--error) 12%, var(--card))", color: "var(--error)" };
  if (key === "warn" || key === "warning") return { background: "color-mix(in srgb, var(--warning) 12%, var(--card))", color: "var(--warning)" };
  if (key === "rate-limit") return { background: "color-mix(in srgb, var(--warning) 14%, var(--card))", color: "var(--warning)" };
  if (key === "firewall") return { background: "color-mix(in srgb, var(--accent) 10%, var(--card))", color: "var(--accent)" };
  return { background: "var(--accent-primary-muted)", color: "var(--accent-primary)" };
}

function SecurityModal({
  title,
  description,
  error,
  busy,
  children,
  onClose,
  onSubmit,
  submitLabel,
}: {
  title: string;
  description: string;
  error: string | null;
  busy: boolean;
  children: React.ReactNode;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
}) {
  const sectionStyle: React.CSSProperties = {
    display: "grid",
    gap: 14,
    padding: 16,
    border: "1px solid var(--table-border)",
    borderRadius: 16,
    background: "var(--muted-bg)",
  };
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15, 23, 42, 0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ width: "100%", maxWidth: 640, borderRadius: "var(--radius-card)", border: `1px solid ${surfaceBorder}`, background: "var(--surface-elevated)", boxShadow: "var(--shadow-overlay)", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${surfaceBorder}`, background: headerBg }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--foreground)" }}>{title}</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>{description}</div>
        </div>
        <div style={{ padding: 22, display: "grid", gap: 18 }}>
          {error ? (
            <div style={{ border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)", background: "color-mix(in srgb, var(--error) 12%, var(--card))", color: "var(--error)", borderRadius: 12, padding: "10px 12px", fontSize: 12 }}>
              {error}
            </div>
          ) : null}
          <div style={sectionStyle}>{children}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "18px 22px 22px", borderTop: `1px solid ${surfaceBorder}` }}>
          <button type="button" onClick={onClose} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: `1px solid ${surfaceBorder}`, background: "var(--card)", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={onSubmit} disabled={busy} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: "1px solid var(--btn-primary-to)", background: "var(--btn-primary-to)", color: "var(--btn-primary-fg)", cursor: "pointer" }}>
            {busy ? "Saving..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SecurityPage() {
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canCreateSecurity = hasModulePermission(user, permissionMap, "security", "create");
  const canEditSecurity = hasModulePermission(user, permissionMap, "security", "edit");
  const canDeleteSecurity = hasModulePermission(user, permissionMap, "security", "delete");
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [keyStatusFilter, setKeyStatusFilter] = useState<"" | "active" | "revoked">("active");
  const [keysPage, setKeysPage] = useState(1);
  const [toast, setToast] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [modal, setModal] = useState<"" | "block">("");
  const [blockForm, setBlockForm] = useState({ ip: "", reason: "", source: "manual" });

  const query = useQuery({
    queryKey: ["security", "global"],
    queryFn: () => getSecurityOverview(),
  });

  const ingestionKeysQuery = useQuery({
    queryKey: ["security", "device-ingestion-keys", search, keyStatusFilter, keysPage],
    queryFn: () =>
      getDeviceIngestionKeys({
        search,
        status_filter: keyStatusFilter,
        page: keysPage,
        page_size: 10,
      }),
  });

  const activeKeyCountQuery = useQuery({
    queryKey: ["security", "device-ingestion-keys", "active-count"],
    queryFn: () =>
      getDeviceIngestionKeys({
        status_filter: "active",
        page: 1,
        page_size: 10,
      }),
  });

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["security"] });
  };

  const createBlockMutation = useMutation({
    mutationFn: () =>
      createBlocklistEntry({
        tenant_id: null,
        ip: blockForm.ip.trim(),
        reason: blockForm.reason.trim(),
        source: blockForm.source,
        status: "blocked",
      }),
    onSuccess: async () => {
      await invalidate();
      setModal("");
      setBlockForm({ ip: "", reason: "", source: "manual" });
      setFormError(null);
      setToast("Blocklist entry created.");
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Failed to save blocklist entry."),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (id: string) => deleteBlocklistEntry(id),
    onSuccess: async () => {
      await invalidate();
      setToast("Blocklist entry removed.");
    },
  });

  const revokeKeyMutation = useMutation({
    mutationFn: (id: string) => revokeDeviceIngestionKey(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["security", "device-ingestion-keys"] });
      setToast("Device ingestion key revoked.");
    },
  });

  const overview = query.data;
  const term = search.trim().toLowerCase();
  const filteredBlocklist = (overview?.blocklist ?? []).filter((entry) => [entry.ip, entry.reason, entry.source, entry.status].join(" ").toLowerCase().includes(term));

  const stats = {
    blocked: overview?.blocklist.length ?? 0,
    activeKeys: activeKeyCountQuery.data?.total ?? 0,
  };

  const totalKeyPages = Math.max(1, Math.ceil((ingestionKeysQuery.data?.total ?? 0) / (ingestionKeysQuery.data?.page_size || 10)));

  function closeModal() {
    setModal("");
    setFormError(null);
  }

  return (
    <EnterprisePageShell
      // kicker="Security"
      title="Security"
      // description="Security controls backed by database records instead of frontend-only seed data."
    >
      <div style={{ display: "grid", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
          {[
            { label: "Blocked IPs", value: stats.blocked, sub: "Active deny entries" },
            { label: "Active keys", value: stats.activeKeys, sub: "Usable integrations" },
          ].map((item) => (
            <div key={item.label} style={{ background: "var(--card)", border: `1px solid ${surfaceBorder}`, borderRadius: 20, padding: "14px 16px", boxShadow: surfaceShadow }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>{item.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--foreground)" }}>{item.value}</div>
              <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>{item.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            className="ui-control"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setKeysPage(1);
            }}
            placeholder="Search IP, device, key, reason..."
            style={{ minWidth: 280 }}
          />
        </div>

        {query.isLoading ? (
          <div style={{ padding: 16, fontSize: 13, color: "#5f6f68" }}>Loading security overview...</div>
        ) : query.isError ? (
          <div style={{ padding: 16, fontSize: 13, color: "var(--error)" }}>
            {query.error instanceof Error ? query.error.message : "Failed to load security overview."}
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gap: 20 }}>
              <section style={{ borderRadius: 20, border: `1px solid ${surfaceBorder}`, background: "var(--card)", overflow: "hidden", boxShadow: surfaceShadow }}>
                <div style={{ padding: "16px 18px", borderBottom: `1px solid ${surfaceBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: headerBg }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--foreground)" }}>Portal blocklist</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>Global portal IP deny rules</div>
                  </div>
                  {canCreateSecurity ? (
                    <button type="button" onClick={() => setModal("block")} style={{ minHeight: 34, padding: "0 12px", borderRadius: 10, border: "1px solid var(--btn-primary-to)", background: "var(--btn-primary-to)", color: "var(--btn-primary-fg)", cursor: "pointer" }}>
                      + Add
                    </button>
                  ) : null}
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: headerBg }}>
                      <tr>
                        {["IP", "Reason", "Source", "Blocked at", "Actions"].map((label) => (
                          <th key={label} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", borderBottom: `1px solid ${surfaceBorder}` }}>
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBlocklist.length ? filteredBlocklist.map((entry) => (
                        <tr key={entry.id}>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>{entry.ip}</td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>{entry.reason}</td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>
                            <span style={{ display: "inline-flex", padding: "5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, ...badgeTone(entry.source) }}>{entry.source}</span>
                          </td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}`, fontSize: 12, color: "var(--muted)" }}>{new Date(entry.blocked_at).toLocaleString()}</td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>
                            {canDeleteSecurity ? (
                              <button type="button" onClick={() => deleteBlockMutation.mutate(entry.id)} style={{ minHeight: 32, padding: "0 10px", borderRadius: 8, border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)", background: "color-mix(in srgb, var(--error) 10%, var(--card))", color: "var(--error)", cursor: "pointer" }}>
                                Remove
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} style={{ padding: "28px 14px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No blocklist rows found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section style={{ borderRadius: 20, border: `1px solid ${surfaceBorder}`, background: "var(--card)", overflow: "hidden", boxShadow: surfaceShadow }}>
                <div style={{ padding: "16px 18px", borderBottom: `1px solid ${surfaceBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: headerBg }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--foreground)" }}>Device ingestion keys</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>Real enforced device telemetry credentials. Revoking a key blocks future ingestion for that device.</div>
                  </div>
                  <div style={{ minWidth: 170 }}>
                    <ThemedSelect
                      value={keyStatusFilter}
                      onChange={(value) => {
                        setKeyStatusFilter(value as "" | "active" | "revoked");
                        setKeysPage(1);
                      }}
                      options={[
                        { value: "active", label: "Active only" },
                        { value: "revoked", label: "Revoked only" },
                        { value: "", label: "All key states" },
                      ]}
                      ariaLabel="Ingestion key status filter"
                      triggerClassName={triggerClassName}
                      menuClassName={menuClassName}
                      optionClassName={optionClassName}
                    />
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: headerBg }}>
                      <tr>
                        {["Device", "Prefix", "Last used", "Status", "Actions"].map((label) => (
                          <th key={label} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", borderBottom: `1px solid ${surfaceBorder}` }}>
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ingestionKeysQuery.isLoading ? (
                        <tr>
                          <td colSpan={5} style={{ padding: "14px" }}>
                            {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={5} />)}
                          </td>
                        </tr>
                      ) : (ingestionKeysQuery.data?.items ?? []).length ? (ingestionKeysQuery.data?.items ?? []).map((entry) => (
                        <tr key={entry.id}>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>
                            <div style={{ fontWeight: 600, color: "var(--foreground)" }}>{entry.device_name || entry.device_uid}</div>
                            <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>{entry.device_uid}</div>
                          </td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}`, fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 12 }}>{entry.key_prefix ?? "—"}</td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}`, fontSize: 12, color: "var(--muted)" }}>{entry.last_used_at ? new Date(entry.last_used_at).toLocaleString() : "Never used"}</td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>
                            <span style={{ display: "inline-flex", padding: "5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, ...badgeTone(entry.status) }}>{entry.status}</span>
                          </td>
                          <td style={{ padding: "13px 14px", borderBottom: `1px solid ${surfaceBorder}` }}>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                              {canEditSecurity && entry.status !== "revoked" ? (
                                <button type="button" onClick={() => revokeKeyMutation.mutate(entry.id)} style={{ minHeight: 32, padding: "0 10px", borderRadius: 8, border: "1px solid color-mix(in srgb, var(--warning) 35%, transparent)", background: "color-mix(in srgb, var(--warning) 12%, var(--card))", color: "var(--warning)", cursor: "pointer" }}>
                                  Revoke
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} style={{ padding: "28px 14px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No device ingestion keys found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderTop: `1px solid ${surfaceBorder}`, background: headerBg }}>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>
                    Page {ingestionKeysQuery.data?.page ?? keysPage} of {totalKeyPages}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="ui-control" disabled={keysPage <= 1} onClick={() => setKeysPage((p) => Math.max(1, p - 1))}>
                      Previous
                    </button>
                    <button type="button" className="ui-control" disabled={keysPage >= totalKeyPages} onClick={() => setKeysPage((p) => Math.min(totalKeyPages, p + 1))}>
                      Next
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}
      </div>

      {modal === "block" && canCreateSecurity ? (
        <SecurityModal
          title="Add blocklist entry"
          description="Create a blocked IP entry stored in the database."
          error={formError}
          busy={createBlockMutation.isPending}
          onClose={closeModal}
          onSubmit={() => {
            if (!blockForm.ip.trim() || !blockForm.reason.trim()) {
              setFormError("IP and reason are required.");
              return;
            }
            createBlockMutation.mutate();
          }}
          submitLabel="Block IP"
        >
          <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
            <span className="form-label form-label-required">IP address</span>
            <input
              className={`ui-control${formError && !blockForm.ip.trim() ? " form-field-invalid" : ""}`}
              value={blockForm.ip}
              onChange={(e) => {
                setBlockForm((p) => ({ ...p, ip: e.target.value }));
                setFormError(null);
              }}
            />
            {formError && !blockForm.ip.trim() ? <span className="form-error">IP address is required.</span> : <span className="form-helper">Enter a single address to block immediately.</span>}
          </label>
          <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
            <span className="form-label form-label-required">Reason</span>
            <textarea
              className={`ui-control${formError && !blockForm.reason.trim() ? " form-field-invalid" : ""}`}
              value={blockForm.reason}
              onChange={(e) => {
                setBlockForm((p) => ({ ...p, reason: e.target.value }));
                setFormError(null);
              }}
              style={{ minHeight: 96 }}
            />
            {formError && !blockForm.reason.trim() ? <span className="form-error">Reason is required.</span> : <span className="form-helper">Capture the trigger so teammates know whether this was abuse, rate limiting, or manual containment.</span>}
          </label>
          <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
            <span className="form-label">Source</span>
            <ThemedSelect
              value={blockForm.source}
              onChange={(value) => {
                setBlockForm((p) => ({ ...p, source: value }));
                setFormError(null);
              }}
              options={[
                { value: "manual", label: "Manual" },
                { value: "rate-limit", label: "Rate limit" },
                { value: "firewall", label: "Firewall" },
              ]}
              ariaLabel="Block source"
              triggerClassName={triggerClassName}
              menuClassName={menuClassName}
              optionClassName={optionClassName}
            />
            <span className="form-helper">Mark whether this came from a manual action, rate limit, or firewall event.</span>
          </label>
        </SecurityModal>
      ) : null}

      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </EnterprisePageShell>
  );
}
